// Résumé du résultat d'une étape (step_done.resume), par exemple {nb_lignes: 128, periode: "2026-09"}.
// La période est omise quand elle reprend les dates déjà affichées dans les paramètres
// (le serveur la déduit des paramètres tant que le MCP ne renvoie pas meta.periode).
// Liste paginée complétée par le serveur : pages réellement réunies (pages), sur combien quand
// certaines sont en échec (pages_total, pages_en_echec). Lecture ciblée d'un résultat volumineux
// (step_done réémis après la lecture) : éléments retenus pour la réponse sur le total lu
// (lecture_retenus, lecture_total).
// Étapes concises (lot 2) : « 128 résultats », « aucun résultat », « 12 retenus sur 340 pour la
// réponse » ; une
// période différente des paramètres s'écrit en français (« 1er–31 oct. 2026 »).

const isPrimitive = (v) => v != null && v !== '' && typeof v !== 'object';
const nombre = (v) => new Intl.NumberFormat('fr-FR').format(v);
const pluriel = (n, mot) => `${mot}${n > 1 ? 's' : ''}`;

export function formatResume(resume, paramsText = '') {
  if (!resume) return '';
  if (typeof resume === 'string') return resume;
  const parts = [];
  for (const [k, v] of Object.entries(resume)) {
    if (!isPrimitive(v)) continue;
    if (k === 'nb_lignes') parts.push(Number(v) === 0 ? 'aucun résultat' : `${nombre(v)} ${pluriel(v, 'résultat')}`);
    else if (k === 'pages' && Number.isInteger(v)) {
      const total = Number.isInteger(resume.pages_total) && resume.pages_total > v ? resume.pages_total : null;
      parts.push(total ? `${v} ${pluriel(v, 'page')} sur ${total} ${pluriel(v, 'réunie')}` : `${v} ${pluriel(v, 'page')} ${pluriel(v, 'réunie')}`);
    } else if (k === 'pages_total') continue;
    else if (k === 'pages_en_echec' && Number.isInteger(v)) parts.push(`${v} ${pluriel(v, 'page')} en échec`);
    else if (k === 'lecture_retenus' && Number.isInteger(v)) {
      const total = Number.isInteger(resume.lecture_total) ? ` sur ${nombre(resume.lecture_total)}` : '';
      parts.push(`${nombre(v)} ${pluriel(v, 'retenu')}${total} pour la réponse`);
    } else if (k === 'lecture_total') continue;
    else if (k === 'periode') {
      const dates = String(v).split('/').filter(Boolean);
      if (!dates.length || !dates.every((date) => paramsText.includes(date))) {
        const [debut, fin] = String(v).split('/');
        parts.push(periodeEtapeFr(debut, fin) || String(v));
      }
    } else parts.push(`${k.replace(/_/g, ' ')} : ${v}`);
  }
  return parts.join(' · ');
}

// ── Période (même écriture que backend/stepLabels.js : formatPeriodeFr, periodeCourteFr) ──
const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

function parseDate(value) {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const [annee, mois, jour] = match.slice(1).map(Number);
  if (mois < 1 || mois > 12 || jour < 1 || jour > 31) return null;
  return { annee, mois, jour };
}

const jourFr = (jour) => (jour === 1 ? '1er' : String(jour));
const dateFr = (d, avecAnnee = true) => `${jourFr(d.jour)} ${MOIS[d.mois - 1]}${avecAnnee ? ` ${d.annee}` : ''}`;

// Heure « 8 h », « 8 h 30 » ; null pour un début (00:00) ou une fin de journée (23:59, 00:00)
function heure(value, fin) {
  if (typeof value !== 'string') return null;
  const match = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/.exec(value.trim());
  if (!match) return null;
  const [h, m] = [Number(match[1]), Number(match[2])];
  if ((h === 0 && m === 0) || (fin && h === 23 && m === 59)) return null;
  return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;
}

/**
 * Période d'une étape : « 1er–31 oct. 2026 » ; `anneeCourante` omise des dates (libellés courts du
 * serveur), heures écrites quand elles bornent la journée (« 28 oct., 8 h – 12 h »). "" sans date.
 */
export function periodeEtapeFr(debut, fin, anneeCourante = null) {
  const d1 = parseDate(debut);
  const d2 = parseDate(fin);
  const h1 = heure(debut, false);
  const h2 = heure(fin, true);
  if (d1 && d2) {
    const avecAnnee = !(d1.annee === anneeCourante && d2.annee === anneeCourante);
    if (h1 || h2) {
      const memeJour = d1.annee === d2.annee && d1.mois === d2.mois && d1.jour === d2.jour;
      if (memeJour) return `${dateFr(d1, avecAnnee)}, ${h1 ?? '0 h'} – ${h2 ?? '24 h'}`;
      return `${dateFr(d1, avecAnnee)} ${h1 ?? '0 h'} – ${dateFr(d2, avecAnnee)} ${h2 ?? '24 h'}`;
    }
    if (d1.annee === d2.annee && d1.mois === d2.mois) {
      if (d1.jour === d2.jour) return dateFr(d1, avecAnnee);
      return `${jourFr(d1.jour)}–${d2.jour} ${MOIS[d2.mois - 1]}${avecAnnee ? ` ${d2.annee}` : ''}`;
    }
    if (d1.annee === d2.annee) return `${dateFr(d1, false)} – ${dateFr(d2, avecAnnee)}`;
    return `${dateFr(d1)} – ${dateFr(d2)}`;
  }
  if (d1) return `depuis le ${dateFr(d1, d1.annee !== anneeCourante)}${h1 ? ` ${h1}` : ''}`;
  if (d2) return `jusqu'au ${dateFr(d2, d2.annee !== anneeCourante)}${h2 ? ` ${h2}` : ''}`;
  return '';
}

// Les dates d'un couple (début, fin) figurent-elles déjà dans le libellé ? Écriture courte (sans
// l'année en cours) ou complète (libellés historiques) ; quand des heures bornent la journée,
// seule une écriture avec ces heures suffit : sinon elles restent lisibles dans les paramètres.
// L'écriture courte est celle de l'année d'AFFICHAGE : un libellé sans année (« 1er–31 déc. »)
// relu une autre année ne couvre plus ses dates, qui restent lisibles, avec l'année, dans la
// ligne des paramètres.
function periodeDansLibelle(label, debut, fin, anneeAffichage = new Date().getFullYear()) {
  if (!parseDate(debut) && !parseDate(fin)) return false;
  return [periodeEtapeFr(debut, fin, anneeAffichage), periodeEtapeFr(debut, fin)].some((texte) => texte && label.includes(texte));
}

// ── Libellé et paramètres affichés ──

/** Libellé affiché : « … » pendant l'action, rien à la fin (libellés historiques compris). */
export function libelleAffiche(label, running) {
  const base = String(label ?? '').replace(/(?:\s*…|\.{3})\s*$/, '');
  return running && base ? `${base}…` : base;
}

const sansAccents = (texte) => texte.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const normalise = (texte) => sansAccents(String(texte).toLowerCase()).replace(/_/g, ' ');
const mots = (texte) => normalise(texte).split(/[^\p{L}\p{N}@.-]+/u).map((mot) => mot.replace(/^[.-]+|[.-]+$/g, '')).filter(Boolean);

// Valeur textuelle déjà lisible dans le libellé : ses mots, dans l'ordre ; un mot peut être le
// début d'un mot du libellé (« expire » → « expiré », « 30j » → « 30 derniers jours »)
function texteDansLibelle(label, valeur) {
  const cherches = mots(valeur).flatMap((mot) => {
    const court = /^(\d+)([a-z])$/.exec(mot);
    return court ? [court[1], court[2]] : [mot];
  });
  const disponibles = mots(label);
  let i = 0;
  for (const mot of cherches) {
    while (i < disponibles.length && !(disponibles[i] === mot || ((mot.length >= 4 || mot.length === 1 || /^\d+$/.test(mot)) && disponibles[i].startsWith(mot)))) i += 1;
    if (i >= disponibles.length) return false;
    i += 1;
  }
  return true;
}

// Paramètres chiffrés que le serveur écrit en clair (backend/stepLabels.js, SEUILS_ETAPE)
const SEUILS = {
  delai_jours: (n) => `délai ${n} jours`,
  jours_alerte: (n) => `alerte à ${n} jours`,
  expire_sous_jours: (n) => `fin sous ${n} jours`,
  duree_arret_min: (n) => `arrêts ≥ ${n} min`,
  duree_coupure_min: (n) => `coupures ≥ ${n} min`,
  vitesse_limite_kmh: (n) => `limite ${n} km/h`,
  limit: (n) => `top ${n}`,
  top: (n) => `top ${n}`,
  top_n: (n) => `top ${n}`,
  limit_vehicules: (n) => `top ${n} véhicules`,
  limit_conducteurs: (n) => `top ${n} conducteurs`,
  max_points: (n) => `${n} points au plus`,
  score_min: (n) => `score ≥ ${n}`,
  score_max: (n) => `score ≤ ${n}`,
  seuil_ecart_pct: (n) => `seuil ${n} %`,
};

// Un paramètre est-il lisible dans le libellé ? Un nombre ou un booléen n'a de sens qu'avec sa
// clé : il faut sa tournure (« top 5 », « délai 30 jours ») ou « clé : valeur » ; jamais un
// chiffre pris au hasard dans une date
function parametreDansLibelle(label, cle, valeur) {
  const texte = normalise(label);
  const brut = typeof valeur === 'boolean' ? (valeur ? 'oui' : 'non') : String(valeur).trim();
  if (normalise(label).includes(normalise(`${cle} : ${brut}`))) return true;
  if (typeof valeur === 'boolean') return false;
  if (/^\d+(?:[.,]\d+)?$/.test(brut)) {
    if (SEUILS[cle]) return texte.includes(normalise(SEUILS[cle](brut)));
    if (cle === 'annee') return new RegExp(`(^|\\D)${brut}(?!\\d)`).test(texte);
    return false;
  }
  return texteDansLibelle(label, brut);
}

const PERIODES = [['date_debut', 'date_fin'], ['periode1_debut', 'periode1_fin'], ['periode2_debut', 'periode2_fin'], ['depuis', 'depuis_fin']];

/**
 * Paramètres de l'étape absents de son libellé (« clé : valeur · … », valeurs simples) : rien pour
 * un libellé court du serveur, qui les porte tous ; ceux d'un libellé historique (« Je récupère
 * les alertes de ce type… ») restent ainsi lisibles dans l'historique des conversations.
 */
export function parametresRestants(label, params, anneeAffichage = new Date().getFullYear()) {
  if (!params) return '';
  const texte = String(label ?? '');
  if (typeof params === 'string') return texteDansLibelle(texte, params) ? '' : params;
  const couvertes = new Set();
  // Type du visuel et format du document : nommés en tête du libellé court (« Tableau : … »,
  // « Document PDF : … ») ; gardés pour un libellé historique (« Construction du visuel — … »)
  if (!/^(?:Construction du visuel|Génération du document)\b/.test(texte)) couvertes.add('visual_type').add('format');
  for (const [debut, fin] of PERIODES) {
    if ((params[debut] || params[fin]) && periodeDansLibelle(texte, params[debut], params[fin], anneeAffichage)) couvertes.add(debut).add(fin);
  }
  return Object.entries(params)
    .filter(([k, v]) => isPrimitive(v) && !couvertes.has(k) && !parametreDansLibelle(texte, k, v))
    .slice(0, 4)
    .map(([k, v]) => `${k.replace(/_/g, ' ')} : ${v}`)
    .join(' · ');
}

// ── Essais successifs d'un même outil ──

// Un nouvel essai porte sur le même sujet : visuels et documents, le même titre (deux visuels
// différents ne se corrigent pas l'un l'autre) ; cartes d'agent, le même outil (champs corrigés
// d'un refus à l'autre) ; les autres outils, le même outil avec les mêmes paramètres hors période
// et pagination (même véhicule, conducteur, type, statut…). Deux véhicules différents ne sont
// jamais deux essais : l'échec du premier reste rouge.
const OUTILS_PAR_TITRE = new Set(['render_visual', 'generate_document']);
const OUTILS_PAR_NOM = new Set(['prepare_agent', 'prepare_agent_change']);
const HORS_SUJET = /^(?:date_|periode|depuis|page|limit|offset)/;
function sujet(params) {
  if (!params || typeof params !== 'object') return String(params ?? '');
  const valeurs = Object.entries(params)
    .filter(([cle, v]) => !HORS_SUJET.test(cle) && v != null && v !== '')
    .map(([cle, v]) => [cle, typeof v === 'object' ? JSON.stringify(v) : String(v)])
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return JSON.stringify(valeurs);
}
function groupeEssai(step) {
  if (OUTILS_PAR_TITRE.has(step.tool)) return `${step.tool}|${libelleAffiche(step.label, false)}`;
  if (OUTILS_PAR_NOM.has(step.tool)) return step.tool;
  return `${step.tool ?? `|${step.label}`}|${sujet(step.params)}`;
}

// L'étape `suivante` a-t-elle commencé après la fin de l'échec ? Les appels d'un même tour du
// modèle commencent tous avant l'exécution du premier (appels parallèles) : aucun n'est un nouvel
// essai d'un autre. Repères propres au flux (endedAt, startedAt) ; absents (historique) : oui.
const apres = (echec, suivante) =>
  !(Number.isFinite(echec.endedAt) && Number.isFinite(suivante.startedAt)) || suivante.startedAt >= echec.endedAt;

/**
 * Essais des étapes (clé → {essai, corrigee}) : `essai` numérote les nouveaux essais d'un outil
 * sur le même sujet après un échec (2e, 3e…) ; `corrigee` marque un échec suivi d'un nouvel essai
 * réussi ou en cours (affiché en neutre, l'erreur reste lisible). Seul l'échec final reste rouge.
 */
export function analyseEssais(items) {
  const steps = items.filter((item) => item.type === 'step');
  const groupes = steps.map(groupeEssai);
  const infos = new Map();
  steps.forEach((step, index) => {
    // Étapes du même sujet terminées avant le début de celle-ci ; la dernière vague (appels d'un
    // même tour, aucune n'étant suivie d'une autre) décide : un échec → essai suivant
    const avant = steps.slice(0, index).filter((s, i) => groupes[i] === groupes[index] && apres(s, step));
    const derniers = avant.filter((s, i) => !avant.slice(i + 1).some((t) => apres(s, t)));
    const echecs = derniers.filter((s) => s.status === 'error').map((s) => infos.get(s.key).essai);
    infos.set(step.key, { essai: echecs.length ? Math.max(...echecs) + 1 : 1, corrigee: false });
  });
  steps.forEach((step, index) => {
    if (step.status !== 'error') return;
    const suivant = steps.find((s, i) => i > index && groupes[i] === groupes[index] && s.status !== 'error' && apres(step, s));
    if (suivant && (suivant.status === 'ok' || suivant.status === 'running')) infos.get(step.key).corrigee = true;
  });
  return infos;
}

// ── En-tête de fin : « Raisonnement · 1 min 12 s · 3 recherches » ──

// Actions réalisées : un brouillon d'e-mail n'est pas un e-mail envoyé (rien ne part sans clic)
const GENRES_ACTION = {
  render_visual: ['visuel', 'visuels'],
  generate_document: ['document', 'documents'],
  prepare_agent: ["carte d'agent", "cartes d'agent"],
  prepare_agent_change: ["carte d'agent", "cartes d'agent"],
  prepare_email: ["brouillon d'e-mail", "brouillons d'e-mail"],
  prepare_email_cancellation: ["annulation d'e-mail", "annulations d'e-mail"],
};
const RECHERCHE = ['recherche', 'recherches'];

/**
 * Actions d'un raisonnement : « 3 recherches · 2 visuels · 1 échec ». Seules les étapes abouties
 * comptent pour leur genre (un visuel refusé n'est pas un visuel) ; échecs finaux et étapes
 * interrompues comptés à part, échecs corrigés par un nouvel essai exclus.
 */
export function resumeActions(items) {
  const infos = analyseEssais(items);
  const comptes = new Map();
  let echecs = 0;
  let interrompues = 0;
  for (const item of items) {
    if (item.type !== 'step' || infos.get(item.key)?.corrigee) continue;
    if (item.status === 'error') { echecs += 1; continue; }
    if (item.status === 'cancelled') { interrompues += 1; continue; }
    const genre = GENRES_ACTION[item.tool] ?? RECHERCHE;
    const actuel = comptes.get(genre[0]) ?? { genre, n: 0 };
    comptes.set(genre[0], { genre, n: actuel.n + 1 });
  }
  const parties = [...comptes.values()].map(({ genre, n }) => `${n} ${n > 1 ? genre[1] : genre[0]}`);
  if (echecs) parties.push(`${echecs} ${echecs > 1 ? 'échecs' : 'échec'}`);
  if (interrompues) parties.push(`${interrompues} ${interrompues > 1 ? 'étapes interrompues' : 'étape interrompue'}`);
  return parties.join(' · ');
}
